import { randomId } from "./randomId";

// Marca durável do navegador, para reconhecer quem já esteve aqui antes. O
// alerta de login (`loginAlert.js`) manda o resultado junto no press & hold: o
// `token` liga as três mensagens de UMA tentativa, este id liga tentativas
// diferentes ao longo do tempo — inclusive a de quem desistiu e voltou dias
// depois.
//
// Valor guardado: `<id>.<primeiraVisitaMs>.<ultimaVisitaMs>.<contagem>`. Tudo
// cabe no cookie de propósito, para que o servidor possa renovar o registro
// inteiro sem entender o formato (ver `sendmail_api.py`).
const COOKIE = "qtx_vid";
// Espelho: cookie e localStorage morrem por motivos diferentes (limpar cookies
// não limpa o storage, e vice-versa). Ler dos dois e escrever nos dois faz o
// que sobreviver reconstruir o outro.
const MIRROR = "qtx_visitor";
// 400 dias é o teto do Chrome para `Max-Age`; pedir mais não estende nada, só
// é silenciosamente cortado.
const MAX_AGE = 400 * 24 * 60 * 60;

const VALUE = /^[a-f0-9]{8,64}\.\d{1,15}\.\d{1,15}\.\d{1,6}$/;

function readCookie() {
  try {
    const prefix = `${COOKIE}=`;
    const hit = document.cookie.split("; ").find((c) => c.startsWith(prefix));
    return hit ? decodeURIComponent(hit.slice(prefix.length)) : "";
  } catch (e) {
    return "";
  }
}

function writeCookie(value) {
  try {
    // `Secure` só em https. Em dev (http://localhost:8080) o navegador
    // DESCARTA em silêncio um cookie marcado como Secure: o `document.cookie`
    // não reclama, a atribuição parece ter funcionado, e na volta não há
    // cookie nenhum — o visitante parece novo a cada vez.
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${COOKIE}=${encodeURIComponent(value)}; Max-Age=${MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  } catch (e) {
    // Cookie bloqueado. O espelho ainda pode pegar.
  }
}

function readMirror() {
  try {
    return localStorage.getItem(MIRROR) || "";
  } catch (e) {
    return "";
  }
}

function writeMirror(value) {
  try {
    localStorage.setItem(MIRROR, value);
  } catch (e) {
    // Modo privado com quota zero, storage bloqueado. Segue sem espelho.
  }
}

function parse(value) {
  if (!VALUE.test(value || "")) return null;
  const [id, first, last, count] = value.split(".");
  return { id, first: Number(first), last: Number(last), count: Number(count) };
}

// "2026-05-03 14:02" no relógio de quem está na tela, montado à mão: o
// `toLocaleString()` seguiria o locale do navegador e o fallback 'en' já
// inverteu dia e mês noutra tela deste repo.
function stamp(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    d.toTimeString().slice(0, 5)
  );
}

// Registra a passagem e devolve os campos do alerta. Chamar uma vez por
// tentativa de login — é ela que incrementa a contagem.
export function touchVisitor() {
  return marcar(1);
}

// A mesma marca, sem contar visita. Existe para o alerta do "learn more"
// (`learnMoreAlert.js`), que dispara em cliques avulsos na tela de login: se
// ele incrementasse, `seen 4x` passaria a significar "clicou em três links e
// tentou entrar uma vez", e a contagem deixaria de responder a única pergunta
// que faz — quantas vezes esta pessoa tentou entrar.
//
// Ainda assim ESCREVE: quando não há marca nenhuma, criar o id aqui é o que
// permite ligar o clique de hoje ao login de amanhã. O preço é que quem clica
// no link antes de tentar entrar aparece como `returning` já na primeira
// tentativa — e está certo: ele esteve aqui mesmo, minutos antes.
export function peekVisitor() {
  return marcar(0);
}

function marcar(incremento) {
  const now = Date.now();
  const known = parse(readCookie()) || parse(readMirror());
  const id = known ? known.id : randomId(8);
  const first = known ? known.first : now;
  const count = (known ? known.count : 0) + incremento;

  const value = `${id}.${first}.${now}.${count}`;
  writeCookie(value);
  writeMirror(value);

  // Só o resumo vai para a mensagem; o cookie fica no navegador. Visitante
  // novo não tem história para contar, e a linha some da mensagem inteira
  // menos o id.
  const history = [`seen ${count}x`, `first ${stamp(first)}`];
  if (known && known.last) history.push(`last ${stamp(known.last)}`);

  return {
    visitorId: id,
    visitor: known ? `returning | ${history.join(" | ")}` : "new",
  };
}

export default touchVisitor;
